import { useEffect, useState } from "react";
import {
  Drawer, Box, Typography, IconButton, Stack, Chip, Divider, CircularProgress,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { getProjectDeployment } from "../utils/api";
import RestrictedBadge from "./RestrictedBadge";

/**
 * Right-side drawer with the deployment details. Surfaced from the project
 * header's "..." menu when the caller is allowed to see it.
 *
 * Technical fields (primary_dataset, primary_run_id, source_run_id, kind,
 * deployment_id, artifacts_gcs_prefix) are gated SERVER-SIDE — the API strips
 * them out for non-admin / non-owner callers. This component just renders
 * what the server returned. If a field is missing, the row is hidden.
 *
 * That means: the gating rule lives in one place (the API) and can't get
 * out of sync with the UI. Don't add UI-only role checks here.
 */
export default function DeploymentDetailsDrawer({ open, onClose, projectId }) {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    getProjectDeployment(projectId)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [open, projectId]);

  const dep = data?.deployment;

  return (
    <Drawer anchor="right" open={open} onClose={onClose}
            PaperProps={{ sx: { width: { xs: "100%", sm: 420 }, p: 2 } }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <Typography variant="h6" fontWeight={700} sx={{ flex: 1 }}>
          Deployment details
        </Typography>
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={24} />
        </Box>
      ) : !dep ? (
        <Typography variant="body2" color="text.secondary">
          No deployment for this project yet.
        </Typography>
      ) : (
        <Stack spacing={2}>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 1.2 }}>
              STATUS
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
              <Chip
                label={dep.status}
                size="small"
                color={dep.status === "active" ? "success" : "default"}
                sx={{ textTransform: "capitalize" }}
              />
              <Typography variant="body2" color="text.secondary">
                Published {fmtDate(dep.created_at)}
              </Typography>
            </Box>
          </Box>

          <Divider />

          {/* TECHNICAL section: only renders rows for fields the server returned.
              The server omits these fields for non-admin / non-owner callers, so
              this whole section disappears for them without any role check here. */}
          {(dep.kind || dep.deployment_id || dep.primary_dataset || dep.primary_run_id
             || dep.source_run_id || dep.artifacts_gcs_prefix) && (
            <>
              <Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 1.2 }}>
                    TECHNICAL
                  </Typography>
                  <RestrictedBadge />
                </Box>
                <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                  {dep.kind && <Row label="Kind" value={dep.kind} mono />}
                  {dep.deployment_id && <Row label="Deployment ID" value={dep.deployment_id} mono />}
                  {dep.primary_dataset && <Row label="Primary dataset" value={dep.primary_dataset} mono />}
                  {dep.primary_run_id && <Row label="Primary run" value={dep.primary_run_id} mono />}
                  {dep.source_run_id && <Row label="Source run" value={dep.source_run_id} mono />}
                  {dep.artifacts_gcs_prefix && <Row label="GCS prefix" value={dep.artifacts_gcs_prefix} mono />}
                </Stack>
              </Box>
              <Divider />
            </>
          )}

          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 1.2 }}>
              SHARED INSTANCES ({(dep.shared_instances || []).length})
            </Typography>
            {(dep.shared_instances || []).length === 0 ? (
              <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, display: "block" }}>
                No shared users yet.
              </Typography>
            ) : (
              <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                {(dep.shared_instances || []).map((inst) => (
                  <Box key={inst.user_id}
                       sx={{ p: 1, border: `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
                    <Typography variant="body2" noWrap>
                      {inst.display_name || inst.email || inst.user_id}
                      {inst.is_admin && <Chip label="admin" size="small" color="primary" variant="outlined"
                                              sx={{ ml: 1, height: 18, fontSize: 10 }} />}
                    </Typography>
                    {inst.email && inst.email !== inst.display_name && (
                      <Typography variant="caption" color="text.secondary" noWrap display="block">
                        {inst.email}
                      </Typography>
                    )}
                    {/* dataset is admin-only — server omits it for non-admins */}
                    {(inst.dataset || inst.status) && (
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Typography variant="caption" color="text.disabled" sx={{ fontFamily: "monospace" }} noWrap>
                          {[inst.dataset, inst.status].filter(Boolean).join(" · ")}
                        </Typography>
                        {inst.dataset && <RestrictedBadge variant="icon" />}
                      </Box>
                    )}
                  </Box>
                ))}
              </Stack>
            )}
          </Box>
        </Stack>
      )}
    </Drawer>
  );
}

function Row({ label, value, mono }) {
  return (
    <Box sx={{ display: "flex", gap: 1 }}>
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 110 }}>
        {label}
      </Typography>
      <Typography variant="body2"
                  sx={{ fontFamily: mono ? "monospace" : undefined, fontSize: mono ? 12 : undefined, wordBreak: "break-all" }}>
        {value || "—"}
      </Typography>
    </Box>
  );
}

function fmtDate(s) {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleString();
  } catch { return s; }
}
